import React, { useState } from "react";
import { Calendar, Clock, Info } from "lucide-react";
import { Card, CardContent } from "src/components/ui/Card";
import { Input } from "src/components/ui/Input";
import { Label } from "src/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/Select";
import { SessionSchedulingType } from "src/services/sessionsService";
import { COMMON_TIMEZONES } from "src/hooks/useTimezone";
import { TutorAvailabilityRadar } from "./TutorAvailabilityRadar";
import { CrossParticipantClocks } from "./CrossParticipantClocks";

export interface ProjectedDateItem {
  dateStr: string;
  weekdayShort: string;
  formatted: string;
}

interface WeekdayOption {
  value: string;
  label: string;
  short: string;
}

interface SessionScheduleTimingCardProps {
  schedulingType: SessionSchedulingType;
  tutor: string;
  tutorName: string;
  tutorTimezone: string;
  studentName: string;
  studentTimezone: string;
  date: string;
  onDateChange: (date: string) => void;
  startDate: string;
  onStartDateChange: (date: string) => void;
  startTime: string;
  onStartTimeChange: (time: string) => void;
  duration: string;
  onDurationChange: (duration: string) => void;
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  weekdays: number[];
  onToggleWeekday: (weekday: number, checked: boolean) => void;
  weekdayOptions: WeekdayOption[];
  selectedWeekdayLabels: string[];
  projectedRecurringDates: ProjectedDateItem[];
  calculatedSeriesEndDate: string;
  onSelectSlot: (slotDate: string, slotLocalTime: string) => void;
}

export const SessionScheduleTimingCard: React.FC<SessionScheduleTimingCardProps> = ({
  schedulingType,
  tutor,
  tutorName,
  tutorTimezone,
  studentName,
  studentTimezone,
  date,
  onDateChange,
  startDate,
  onStartDateChange,
  startTime,
  onStartTimeChange,
  duration,
  onDurationChange,
  timezone,
  onTimezoneChange,
  weekdays,
  onToggleWeekday,
  weekdayOptions,
  selectedWeekdayLabels,
  projectedRecurringDates,
  calculatedSeriesEndDate,
  onSelectSlot,
}) => {
  const [stepSubView, setStepSubView] = useState<"manual" | "calendar">("calendar");

  return (
    <Card className="border border-slate-200 rounded-xl shadow-xs overflow-hidden bg-white">
      <div className="bg-[#faf9fe] px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="text-xs font-mono text-purple-700 font-medium">
            STEP 3 • {schedulingType === "recurring" ? "SERIES RECURRENCE & AVAILABILITY" : "SESSION TIMING & COLLISION RADAR"}
          </div>
          <h2 className="text-base font-bold text-slate-900">
            {schedulingType === "recurring"
              ? "Weekly Cadence, Recurrence Range & Capacity Allocation"
              : "Schedule, Timing & Tutor Availability Radar"}
          </h2>
        </div>
        {schedulingType === "recurring" && (
          <span className="text-xs font-mono px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 font-semibold">
            {projectedRecurringDates.length} Sessions Series
          </span>
        )}
      </div>

      <CardContent className="p-6 space-y-6">
        {/* TUTOR NOT SELECTED WARNING BANNER */}
        {!tutor && (
          <div className="p-3.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-center gap-2.5">
            <Info className="w-4 h-4 text-amber-600 shrink-0" />
            <span>
              <strong>Tutor not yet selected:</strong> Choose a tutor in Step 2 above to inspect their live calendar availability radar and collision status.
            </span>
          </div>
        )}

        {/* INPUT METHOD TOGGLE: MANUAL VS CALENDAR TRIAGE */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
          <div>
            <span className="text-xs font-bold text-slate-800 uppercase tracking-wide block">
              Scheduling Method
            </span>
            <span className="text-xs text-slate-500">
              {schedulingType === "recurring"
                ? "Configure series days manually or scan tutor's multi-week availability grid"
                : "Pick a slot manually or visually inspect tutor's availability matrix"}
            </span>
          </div>
          <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5 text-xs font-semibold self-start sm:self-auto">
            <button
              type="button"
              onClick={() => setStepSubView("manual")}
              className={`px-3 py-1.5 rounded-md transition flex items-center gap-1.5 ${
                stepSubView === "manual"
                  ? "bg-purple-600 text-white shadow-2xs font-bold"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Clock className="w-3.5 h-3.5" />
              <span>Manual Entry</span>
            </button>
            <button
              type="button"
              onClick={() => setStepSubView("calendar")}
              className={`px-3 py-1.5 rounded-md transition flex items-center gap-1.5 ${
                stepSubView === "calendar"
                  ? "bg-purple-600 text-white shadow-2xs font-bold"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              <Calendar className="w-3.5 h-3.5" />
              <span>Calendar Availability</span>
            </button>
          </div>
        </div>

        {/* CALENDAR TRIAGE GRID VIEW */}
        {stepSubView === "calendar" && (
          <TutorAvailabilityRadar
            tutorName={tutor ? tutorName : "Dr. Sarah Thompson (Tutor)"}
            tutorTimezone={tutorTimezone}
            studentName={studentName}
            duration={duration}
            date={date}
            startTime={startTime}
            onSelectSlot={onSelectSlot}
            schedulingType={schedulingType}
            weekdays={weekdays}
            onToggleWeekday={(day) => onToggleWeekday(day, !weekdays.includes(day))}
            projectedDatesCount={projectedRecurringDates.length}
            calculatedSeriesEndDate={calculatedSeriesEndDate}
            startDate={startDate}
          />
        )}

        {/* MANUAL FORM INPUTS */}
        <div className="space-y-4">
          {schedulingType === "recurring" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold text-slate-800 uppercase tracking-wide">
                  Weekly Cadence (Days of Recurrence)
                </Label>
                <span className="text-xs text-purple-700 font-semibold">
                  {selectedWeekdayLabels.length} Day{selectedWeekdayLabels.length === 1 ? "" : "s"} Selected ({selectedWeekdayLabels.join(", ")})
                </span>
              </div>

              {/* Day Pill Selector */}
              <div className="grid grid-cols-7 gap-2">
                {weekdayOptions.map((day) => {
                  const dayValue = Number(day.value);
                  const isSelected = weekdays.includes(dayValue);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => onToggleWeekday(dayValue, !isSelected)}
                      className={`py-2.5 text-center rounded-lg border text-xs transition flex flex-col items-center justify-center ${
                        isSelected
                          ? "border-2 border-purple-600 bg-purple-50 text-purple-700 font-bold shadow-2xs"
                          : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 font-medium"
                      }`}
                    >
                      <span>{day.short}</span>
                      <span className="text-xs font-mono">
                        {isSelected ? "✓ Active" : "Off"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            {/* START DATE */}
            <div className="space-y-1.5 sm:col-span-1">
              <Label htmlFor="sessionDate" className="text-xs font-semibold text-slate-700">
                {schedulingType === "recurring" ? "Series Start Date" : "Session Date"}
              </Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  id="sessionDate"
                  type="date"
                  className="pl-9 h-9 text-xs font-medium rounded-lg border-slate-300"
                  value={schedulingType === "recurring" ? startDate : date}
                  onChange={(e) => {
                    if (schedulingType === "recurring") {
                      onStartDateChange(e.target.value);
                    } else {
                      onDateChange(e.target.value);
                    }
                  }}
                  required
                />
              </div>
            </div>

            {/* START TIME */}
            <div className="space-y-1.5 sm:col-span-1">
              <Label htmlFor="startTime" className="text-xs font-semibold text-slate-700">
                Start Time ({timezone?.split("/")[1]?.replace("_", " ") || "Local"})
              </Label>
              <div className="relative">
                <Clock className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  id="startTime"
                  type="time"
                  className="pl-9 h-9 text-xs font-mono font-medium rounded-lg border-slate-300"
                  value={startTime}
                  onChange={(e) => onStartTimeChange(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* DURATION */}
            <div className="space-y-1.5 sm:col-span-1">
              <Label htmlFor="duration" className="text-xs font-semibold text-slate-700">Duration</Label>
              <Select value={duration} onValueChange={onDurationChange}>
                <SelectTrigger className="h-9 text-xs font-medium rounded-lg border-slate-300 bg-white">
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">60 minutes (1 hr)</SelectItem>
                  <SelectItem value="90">90 minutes (1.5 hrs)</SelectItem>
                  <SelectItem value="120">120 minutes (2 hrs)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* CALCULATED HORIZON OR TIMEZONE */}
            <div className="space-y-1.5 sm:col-span-1">
              <Label className="text-xs font-semibold text-slate-700">
                {schedulingType === "recurring" ? "Calculated Series End" : "Timezone Anchor"}
              </Label>
              {schedulingType === "recurring" ? (
                <div className="h-9 px-3 flex items-center text-xs font-mono font-bold bg-slate-50 border border-slate-200 rounded-lg text-purple-900">
                  {calculatedSeriesEndDate}
                </div>
              ) : (
                <Select value={timezone} onValueChange={onTimezoneChange}>
                  <SelectTrigger className="h-9 text-xs font-medium rounded-lg border-slate-300 bg-white">
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>

        {/* SYNCHRONIZED CLOCKS RADAR */}
        <CrossParticipantClocks
          startTime={startTime}
          timezone={timezone}
          tutorTimezone={tutorTimezone}
          studentTimezone={studentTimezone}
          isRecurring={schedulingType === "recurring"}
        />

        {/* PROJECTED SESSIONS MATRIX (RECURRING MODE) */}
        {schedulingType === "recurring" && projectedRecurringDates.length > 0 && (
          <div className="space-y-2 pt-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-bold text-slate-800 uppercase tracking-wide">
                Projected Series Allocation ({projectedRecurringDates.length} Sessions)
              </Label>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono">
              {projectedRecurringDates.map((item, idx) => {
                const isLast = idx === projectedRecurringDates.length - 1;
                return (
                  <div
                    key={item.dateStr}
                    className={`p-2.5 rounded-lg border flex items-center justify-between ${
                      isLast
                        ? "border-2 border-emerald-400 bg-emerald-50/50 text-emerald-950"
                        : "border-slate-200 bg-white text-slate-800"
                    }`}
                  >
                    <span className="font-bold">
                      #{idx + 1}: {item.formatted}
                    </span>
                    <span className="text-xs text-purple-700 font-semibold">
                      {startTime}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

